import { BaseLayouter } from './BaseLayouter';
import { LayoutData } from '../LayoutData';
import { POI } from '../../poi/POI';
import * as THREE from 'three';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { SpacingMode } from '../LayoutData';

// Global reference for ModernLayouters
declare const ModernLayouters: any;

/**
 * Base class for all Modern Layouters that use TestField JavaScript integration
 * Provides common applyToObjects logic, subclasses only need to specify which JS function to call
 */
export abstract class BaseModernLayouter extends BaseLayouter {
    
    /**
     * ✅ COMMON APPLY LOGIC - Same for all Modern Layouters
     */
    applyToObjects(pois: POI[], layoutData: LayoutData): void {
        const layouterName = this.getLayouterName();
        //console.log(`🚀 ${layouterName}: Applying TestField JavaScript layout to ${pois.length} POIs`);

        // ✅ CLEAN APPROACH: Only use JavaScript ModernLayouters, no fallback
        if (!ModernLayouters) {
            throw new Error(`❌ CRITICAL: ModernLayouters JavaScript class not loaded! Modern layouters require TestField JavaScript integration.`);
        }

        // Get config from subclass
        const config = this.createConfig(pois, layoutData);
        //console.log(`🎯 Config:`, config);

        // Get the specific ModernLayouters function from subclass
        const layouterFunction = this.getModernLayouterFunction();
        
        // 🚀 Direct call to our TestField JavaScript implementation
        const transforms = layouterFunction(config);
        //console.log(`✅ Generated ${transforms.length} transforms`);

        // Apply transforms to POIs (same logic for all Modern Layouters)
        transforms.forEach((transform: any, index: number) => {
            if (index < pois.length) {
                const poi = pois[index];
                
                // Apply position
                poi.poidata.position.value = new Vector3(
                    transform.position.x,
                    transform.position.y,
                    transform.position.z
                );
                
                // Apply rotation (convert from radians to degrees)
                poi.poidata.rotation.value = new Vector3(
                    transform.rotation.x * 180 / Math.PI,
                    transform.rotation.y * 180 / Math.PI,
                    transform.rotation.z * 180 / Math.PI
                );
                
                // Apply scale if provided
                if (transform.scale) {
                    poi.poidata.scale.value = Math.max(transform.scale.x, transform.scale.y, transform.scale.z);
                }
            }
        });
        
        //console.log(`✅ ${layouterName}: Applied TestField JavaScript layout successfully`);
    }

    /**
     * ✅ MODERN APPROACH: Only JavaScript implementation, no legacy methods needed
     */
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        throw new Error(`❌ ${this.getLayouterName()} uses only JavaScript implementation via applyToObjects(). Legacy calculatePosition() not supported.`);
    }

    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        throw new Error(`❌ ${this.getLayouterName()} uses only JavaScript implementation via applyToObjects(). Legacy calculateOrientation() not supported.`);
    }

    calculateOrientationQuaternion(position: Vector3, index: number, totalCount: number, layoutData: LayoutData): Quaternion {
        throw new Error(`❌ ${this.getLayouterName()} uses only JavaScript implementation via applyToObjects(). Legacy calculateOrientationQuaternion() not supported.`);
    }

    getFixedSpacingParameter(layoutData: LayoutData): number {
        throw new Error(`❌ ${this.getLayouterName()} uses only JavaScript implementation via applyToObjects(). Legacy getFixedSpacingParameter() not supported.`);
    }

    calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        throw new Error(`❌ ${this.getLayouterName()} uses only JavaScript implementation via applyToObjects(). Legacy calculateUniformSpacing() not supported.`);
    }

    // ========================================
    // ABSTRACT METHODS - Subclasses must implement
    // ========================================

    /**
     * Return the specific ModernLayouters JavaScript function to call
     * e.g., ModernLayouters.line, ModernLayouters.circle, etc.
     */
    protected abstract getModernLayouterFunction(): (config: any) => any[];

    /**
     * Create the config object for the JavaScript layouter function
     */
    protected abstract createConfig(pois: POI[], layoutData: LayoutData): any;

    /**
     * Get the human-readable name for logging
     */
    protected abstract getLayouterName(): string;

    // ========================================
    // HELPER METHODS
    // ========================================

    /**
     * Helper to get spacing mode string for JavaScript
     */
    protected getSpacingModeString(layoutData: LayoutData): 'margin' | 'distribute' {
        return layoutData.spacingMode?.value === SpacingMode.Fixed ? 'margin' : 'distribute';
    }

    /**
     * Helper to get flip parameter
     */
    protected getFlipParameter(layoutData: LayoutData): boolean {
        return this.getParameter(layoutData, 'modernFlip180', false);
    }

    /**
     * Helper to get parameter value from LayoutData with type safety
     */
    protected getParameter<T>(layoutData: LayoutData, propertyName: string, defaultValue: T): T {
        const property = (layoutData as any)[propertyName];
        return property?.value !== undefined ? property.value : defaultValue;
    }

    /**
     * 🎯 Extract REAL mesh dimensions from THREE.js geometry bounding box (Object Space)
     * This provides accurate width/height/aspect ratio for adaptive layouters
     */
    protected extractMeshDimensions(poi: POI): { width: number, height: number, aspectRatio: number } {
        try {
            // 🎯 SIMPLE FALLBACK: Use shape type + scale for now
            // TODO: Access actual mesh geometry when POI mesh access is clarified
            
            const shape = poi.poidata.shape?.value || 'box';
            const scale = poi.poidata.scale?.value || 1.0;
            
            // Basic shape dimensions (before scaling)
            let baseWidth = 1.0;
            let baseHeight = 1.0;
            
            // Shape-specific aspect ratios
            switch (shape.toLowerCase()) {
                case 'sphere':
                    baseWidth = baseHeight = 1.0; // Sphere is 1:1
                    break;
                case 'box':
                case 'cube':
                    baseWidth = baseHeight = 1.0; // Default cube is 1:1
                    break;
                case 'plane':
                case 'rectangle':
                    // TODO: Extract actual plane aspect ratio from geometry
                    baseWidth = 1.6; // Assume 16:10 aspect ratio for now
                    baseHeight = 1.0;
                    break;
                case 'cylinder':
                    baseWidth = baseHeight = 1.0; // Cylinder diameter = height
                    break;
                default:
                    baseWidth = baseHeight = 1.0;
            }
            
            // Apply uniform scale
            const finalWidth = baseWidth * scale;
            const finalHeight = baseHeight * scale;
            const aspectRatio = finalWidth / finalHeight;
            
            //console.log(`🎯 [${this.getLayouterName()}] POI ${poi.name} (${shape}): w=${finalWidth.toFixed(3)}, h=${finalHeight.toFixed(3)}, aspect=${aspectRatio.toFixed(3)}`);
            
            return {
                width: finalWidth,
                height: finalHeight,
                aspectRatio: aspectRatio
            };
            
        } catch (error) {
            console.error(`❌ [${this.getLayouterName()}] Error extracting mesh dimensions for POI ${poi.name}:`, error);
            return { width: 1.0, height: 1.0, aspectRatio: 1.0 };
        }
    }
}